/**
 * Health samples — `GET /api/v3/health/metrics` · `POST /api/v3/health/samples`
 * (integration guide §5).
 *
 * Outside the `/org` prefix ON PURPOSE: this is the member's own data, theirs
 * whether or not they belong to an organization; only the challenge layer reads
 * it in an org context.
 *
 * ONE DAILY TOTAL per metric per day — not a raw stream. The server upserts on
 * `(user, metric, date, source)`, so re-syncing the same day as the count grows
 * overwrites rather than accumulating.
 */

/** Every source the server accepts. `google_fit` is historical — see {@link ANDROID_HEALTH_SOURCE}. */
export type OrgHealthSource = 'apple_health' | 'google_fit' | 'health_connect' | 'seha' | 'manual';

/**
 * On Android send `health_connect`, not `google_fit` (§5.1). Both are accepted —
 * `google_fit` names a real historical store and retiring it would reject rows
 * already saved under it on re-sync — but they are different stores and the
 * member's history should say which one it was.
 */
export const ANDROID_HEALTH_SOURCE: OrgHealthSource = 'health_connect';

/** The default the server applies when a row carries no `source`. */
export const DEFAULT_HEALTH_SOURCE: OrgHealthSource = 'manual';

export interface OrgHealthMetric {
  key: string;
  /** Ready-to-render Arabic unit («خطوة» · «دقيقة»). */
  unit: string;
  label: string;
}

export interface OrgHealthCatalog {
  /** Server-driven so adding a metric needs no app release. `water_liters` is deliberately absent. */
  metrics: readonly OrgHealthMetric[];
  sources: readonly string[];
  /** Rows older than this are rejected with `too_old`. */
  maxBackfillDays: number;
  /** Hard cap on one `POST /health/samples` batch. */
  maxBatch: number;
  /** Ready-to-render privacy sentence — show it wherever samples are offered. */
  privacy: string;
}

export const EMPTY_HEALTH_CATALOG: OrgHealthCatalog = {
  metrics: [],
  sources: [],
  maxBackfillDays: 30,
  maxBatch: 200,
  privacy: '',
};

/** One daily total. `recordedAt` is optional metadata, not the bucketing key. */
export interface OrgHealthSample {
  metric: string;
  /** `YYYY-MM-DD`. */
  date: string;
  value: number;
  source?: OrgHealthSource;
  /** ISO 8601. */
  recordedAt?: string;
}

export interface OrgHealthRejection {
  /** 0-based position in the array YOU SENT (§5.2). */
  index: number;
  reason: string;
}

/**
 * PARTIAL ACCEPTANCE is the design — every row is judged on its own, and
 * `accepted: 0` is a perfectly ordinary 200 (trap 11). Never infer success from
 * the status code; read {@link accepted} / {@link rejected}.
 */
export interface OrgHealthSyncResult {
  accepted: number;
  rejected: readonly OrgHealthRejection[];
  /** Metrics whose challenges were re-scored — the signal to refresh the engagement tab. */
  creditedMetrics: readonly string[];
}

export const EMPTY_HEALTH_SYNC_RESULT: OrgHealthSyncResult = {
  accepted: 0,
  rejected: [],
  creditedMetrics: [],
};

/** Did anything at all land? A 200 with zero accepted rows is a failed sync in UX terms. */
export function healthSyncLanded(result: OrgHealthSyncResult): boolean {
  return result.accepted > 0;
}

/**
 * Split a batch to the catalogue's `max_batch`, so a large backfill never trips
 * the 422 instead of syncing.
 */
export function chunkHealthSamples(
  samples: readonly OrgHealthSample[],
  maxBatch: number,
): readonly (readonly OrgHealthSample[])[] {
  const size = Math.max(1, maxBatch);
  const out: OrgHealthSample[][] = [];
  for (let i = 0; i < samples.length; i += size) out.push(samples.slice(i, i + size));
  return out;
}
